import React from 'react';
import { FigureLegendPanel } from '../../core/music/vex/FigureLegendPanel';
import type { CanvasBreakdownPayload } from '../../core/types/payloads/canvas_breakdown.types';import { VexRhythmStrip } from '../../core/music/vex/VexRhythmStrip';
import { VexPhraseScore } from '../../core/music/vex/VexPhraseScore';
import { buildPhraseScoreBars, phraseScoreCaption } from '../../core/music/vex/phraseScoreBridge';
import type { MusicalToken } from '../../core/types/music';

function stripForBar(
  payload: CanvasBreakdownPayload,
  barNum: number,
  tokens: MusicalToken[],
  tokenStart: number,
  tokenEnd: number,
) {
  const byBar = payload.scoreEventsByBar;
  const scoreEvents = byBar?.[barNum] ?? (payload.targetBarNumber === barNum ? payload.scoreEvents : undefined);
  const globalDots = payload.rhythmDots ?? [];
  const globalTies = payload.modifiers?.ties ?? [];
  const dottedIndices = globalDots
    .filter((i) => i >= tokenStart && i < tokenEnd)
    .map((i) => i - tokenStart);
  const ties = globalTies
    .filter(([from, to]) => from >= tokenStart && to < tokenEnd)
    .map(([from, to]) => [from - tokenStart, to - tokenStart] as [number, number]);

  return (
    <VexRhythmStrip
      key={barNum}
      pattern={scoreEvents?.length ? scoreEvents.map((e) => e.token) : tokens}
      scoreEvents={scoreEvents}
      timeSignature={payload.timeSignature ?? '4/4'}
      readingMethod={payload.rhythmMethod ?? 'tradicional'}
      targetBarNumber={barNum}
      dottedIndices={dottedIndices}
      ties={ties}
      keySignature={payload.modifiers?.keySignature}
      printMode={false}
    />
  );
}

export function CanvasBreakdownReadView({
  payload,
}: {
  payload: CanvasBreakdownPayload;
}) {
  const tokens = payload.patternTokens ?? [];
  const phraseBars = payload.phraseBarNumbers?.length ? payload.phraseBarNumbers : null;
  const barStarts = payload.barTokenStarts;
  const figureEntries = payload.figureLegendEntries ?? [];
  const figureLegend = payload.figureLegend ?? [];
  const ts = payload.timeSignature ?? '4/4';

  const phraseScoreBars =
    phraseBars && phraseBars.length > 1
      ? buildPhraseScoreBars({
          phraseBarNumbers: phraseBars,
          barTokenStarts: barStarts,
          patternTokens: tokens,
          rhythmDots: payload.rhythmDots,
          scoreEventsByBar: payload.scoreEventsByBar,
          modifiers: payload.modifiers,
        })
      : null;

  return (
    <div className="canvas-breakdown-view rhythm-workshop">
      <h3 className="eval-block__title">{payload.title}</h3>
      {payload.sectionTitle && payload.sectionTitle !== payload.title && (
        <p className="text-sm text-[var(--sala-ink-muted)]">{payload.sectionTitle}</p>
      )}
      <p className="eval-block__body">{payload.bodyText}</p>

      {figureEntries.length > 0 ? (
        <FigureLegendPanel entries={figureEntries} />
      ) : figureLegend.length > 0 ? (
        <div className="canvas-breakdown-view__legend">
          <p className="canvas-breakdown-view__legend-title">Figuras en esta obra</p>
          <ul className="canvas-breakdown-view__legend-list">
            {figureLegend.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {tokens.length > 0 && (
        <>
          {phraseScoreBars ? (
            <VexPhraseScore
              bars={phraseScoreBars}
              timeSignature={ts}
              keySignature={payload.modifiers?.keySignature}
              caption={phraseScoreCaption(phraseBars!, ts)}
            />
          ) : phraseBars ? (
            phraseBars.map((barNum, i) => {
              const start = barStarts?.[i] ?? 0;
              const end = barStarts?.[i + 1] ?? tokens.length;
              const slice = tokens.slice(start, end);
              return stripForBar(payload, barNum, slice, start, end);
            })
          ) : (
            stripForBar(payload, payload.targetBarNumber ?? 1, tokens, 0, tokens.length)
          )}
        </>
      )}
    </div>
  );
}
